import { Group } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import * as _ from "lodash-es";
import { DateTime } from "luxon";
import type {
  MRT_ColumnDef,
  MRT_SortingState,
  MRT_TableOptions
} from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { Userpilot } from "userpilot";
import { IconButton } from "../../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { DeleteButton } from "../../../../mantine/mrt/components/rowActions/DeleteButton";
import { EditButton } from "../../../../mantine/mrt/components/rowActions/EditButton";
import { HistoryButton } from "../../../../mantine/mrt/components/rowActions/HistoryButton";
import { convertApiErrorsToMrtErrors } from "../../../../mantine/mrt/utils/convertApiErrorsToMrtErrors";
import { useToast } from "../../../../mantine/notifications/useToast";
import { emptyArray } from "../../../../utils/constants";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { downloadAsExcel } from "../../../../utils/files/downloadAsExcel";
import { DeleteConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import { NoDataMessage } from "../../../BuildingBlocks/NoDataMessage/NoDataMessage";
import { NoEntries } from "../../../NoEntries/NoEntries";
import { useEnergyRevenuesPageParams } from "../../hooks/useEnergyRevenuesPageParams";
import { useEnergyRevenuesVerificationMutations } from "../../hooks/useEnergyRevenuesVerificationMutations";
import type {
  EnergyRevenuesCellChecksResponse,
  EnergyRevenuesCellCheckState,
  EnergyRevenuesCellCheckStateResponse,
  EnergyRevenuesListResponse
} from "../../RevenueMonitoring.types";
import { REVENUE_TABLE_HEADER_TEXTS } from "../EnergyRevenuesTable/EnergyRevenuesTable";
import { prepareVerificationTableDataForExport } from "../utils/prepareVerificationTableDataForExport";
import { getCalculatedAmountColumn } from "./columns/getCalculatedAmountColumn";
import { getDiffColumn } from "./columns/getDiffColumn";
import { getPeriodStartColumn } from "./columns/getPeriodStartColumn";
import { getStatusColumn } from "./columns/getStatusColumn";
import { getUserProvidedAmountColumn } from "./columns/getUserProvidedAmountColumn";
import "./EnergyRevenuesVerificationTable.scss";
import { EneryRevenuesVerificationTableBatchActions } from "./EneryRevenuesVerificationTableBatchActions";
import { VerificationHistoryModal } from "./VerificationHistoryModal/VerificationHistoryModal";

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "period_start",
    desc: true
  },
  {
    id: "column",
    desc: false
  }
];

const getVerificationLabel = (count?: number, article?: boolean) => {
  if (!count) return "";
  if (count === 1) return `${article ? "diesen" : ""} Prüfeintrag`;
  return `${article ? "diese" : ""} ${count} Prüfeinträge`;
};

interface EnergyRevenuesVerificationTableProps {
  companyId: number;
  dataVerificationItems?: EnergyRevenuesCellChecksResponse;
  energyRevenuesData?: EnergyRevenuesListResponse;
  exportFileName?: string;
  isLoading?: boolean;
  toolbarComponents?: React.ReactNode;
}

function EnergyRevenuesVerificationTable({
  companyId,
  dataVerificationItems,
  isLoading,
  energyRevenuesData,
  exportFileName,
  toolbarComponents
}: EnergyRevenuesVerificationTableProps) {
  const { showToast, showErrorToast } = useToast();
  const {
    verificationSort,
    setVerificationSort,
    verificationFilters,
    setVerificationFilters,
    verificationColumnVisibility,
    setVerificationColumnVisibility
  } = useEnergyRevenuesPageParams();
  const { updateVerificationMutation, deleteVerificationMutation } =
    useEnergyRevenuesVerificationMutations(companyId);

  const isPending =
    updateVerificationMutation.isPending ||
    deleteVerificationMutation.isPending;

  const [historyItem, setHistoryItem] = useState<
    EnergyRevenuesCellCheckStateResponse | undefined
  >();

  const [verificationItemsToDelete, setVerificationItemsToDelete] = useState<
    Array<EnergyRevenuesCellCheckStateResponse> | undefined
  >();

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});

  function handleVerificationTableExport() {
    const exportData = prepareVerificationTableDataForExport(table);
    downloadAsExcel(
      exportData,
      `${exportFileName}_Prüfung_${DateTime.now().toFormat("yyyyLLdd")}`
    );
  }

  const handleSaveVerificationItem: MRT_TableOptions<EnergyRevenuesCellCheckStateResponse>["onEditingRowSave"] =
    async ({ row, values, table }) => {
      setValidationErrors({});

      const valuesWithoutReadOnlyProps = _.omit(values, [
        "timestamp_of_state_change",
        "diff"
      ]);

      const updatedItem: EnergyRevenuesCellCheckState = {
        ...valuesWithoutReadOnlyProps,
        period_start: row.original.period_start,
        column: row.original.column
      };

      try {
        await updateVerificationMutation.mutateAsync({
          verificationItem: updatedItem,
          verificationId: row.original.id
        });

        table.setEditingRow(null);
      } catch (error) {
        if (error.response?.status === 400) {
          const validationErrors = convertApiErrorsToMrtErrors(
            error.response.data
          );

          if (Object.keys(validationErrors).length > 0) {
            setValidationErrors(validationErrors);
          } else {
            showErrorToast(error);
          }
        } else {
          showErrorToast(error);
        }
      }
    };

  const handleChangeStatusBatchVerificationItems = async (
    value: EnergyRevenuesCellCheckState["state"]
  ) => {
    Userpilot.track("em_prufansicht_status_andern_button", {
      new_status: value
    });
    const selectedRows = table
      .getSelectedRowModel()
      .flatRows.map((row) => row.original);

    const results = await Promise.allSettled(
      selectedRows.map((row) =>
        updateVerificationMutation.mutateAsync(
          {
            verificationId: row.id,
            verificationItem: {
              period_start: row.period_start,
              column: row.column,
              state: value,
              user_provided_amount: row.user_provided_amount,
              notes: row.notes,
              person_id: row.person_id,
              calculated_amount: row.calculated_amount
            }
          },
          {
            onError: (error) => {
              showErrorToast(error);
            }
          }
        )
      )
    );
    table.resetRowSelection();
    const successCount = results.filter((r) => r.status === "fulfilled").length;
    showToast({
      type: "success",
      message: `${successCount} ${successCount > 1 ? "Prüfungseinträge" : "Prüfungseintrag"} aktualisiert.`
    });
  };

  const handleDeleteBatchVerificationItems = () => {
    const selectedRows = table
      .getSelectedRowModel()
      .flatRows.map((row) => row.original);
    setVerificationItemsToDelete(selectedRows);
  };

  const handleDeleteVerificationItems = async () => {
    if (!verificationItemsToDelete || verificationItemsToDelete.length === 0)
      return;
    const results = await Promise.allSettled(
      verificationItemsToDelete.map((row) =>
        deleteVerificationMutation.mutateAsync(row.id, {
          onError: (error) => {
            showErrorToast(error);
          }
        })
      )
    );
    table.resetRowSelection();
    const successCount = results.filter((r) => r.status === "fulfilled").length;
    showToast({
      type: "success",
      message: `${successCount} ${successCount > 1 ? "Prüfungseinträge" : "Prüfungseintrag"} gelöscht.`
    });
    setVerificationItemsToDelete(undefined);
  };

  const columns = useMemo<
    MRT_ColumnDef<EnergyRevenuesCellCheckStateResponse>[]
  >(
    () => [
      {
        header: "Bezeichnung",
        accessorKey: "label",
        mantineEditTextInputProps: () => ({
          "aria-label": "Bezeichnung",
          onClick: () => Userpilot.track("em_prufansicht_bezeichnung_feld")
        }),
        enableColumnFilter: false
      },
      getPeriodStartColumn(),
      {
        header: "Typ",
        id: "column",
        accessorFn: (row) => REVENUE_TABLE_HEADER_TEXTS[row.column],
        enableEditing: false,
        enableColumnFilterModes: false,
        enableColumnFilter: true,
        filterVariant: "multi-select",
        mantineFilterMultiSelectProps: {
          data: Object.values(REVENUE_TABLE_HEADER_TEXTS)
        }
      },
      getStatusColumn(),
      getCalculatedAmountColumn({
        energyRevenuesData
      }),
      getUserProvidedAmountColumn(validationErrors?.user_provided_amount),
      getDiffColumn(),
      {
        header: "Notizen",
        accessorKey: "notes",
        mantineEditTextInputProps: () => ({
          "aria-label": "Notizen",
          onClick: () => Userpilot.track("em_prufansicht_notizen_feld")
        }),
        enableColumnFilter: false
      },
      {
        header: "Letzte Änderung",
        id: "timestamp_of_state_change",
        accessorFn: (row) =>
          backendDateOrDateTimeToLuxonDateTime(
            row.timestamp_of_state_change
          ).toJSDate(),
        Cell: ({ cell }) =>
          DateTime.fromJSDate(cell.getValue<Date>()).toLocaleString(),
        enableColumnFilter: false,
        enableEditing: false
      }
    ],
    [validationErrors, energyRevenuesData]
  );

  const tableOptions = {
    ...getDefaultMRTOptions<EnergyRevenuesCellCheckStateResponse>({
      initialState: {
        showColumnFilters: true,
        showGlobalFilter: true,
        pagination: {
          pageIndex: 0,
          pageSize: 25
        }
      },
      hideBorder: true,
      className: "EnergyRevenuesVerificationTable"
    }),
    ...getDefaultMRTRowEditModeOptions({
      displayColumnDefOptions: {
        "mrt-row-actions": {
          size: 100
        }
      }
    }),
    enableGlobalFilter: true,
    enableFacetedValues: true,
    columnFilterDisplayMode: "popover",
    enableColumnFilterModes: true,
    state: {
      sorting: verificationSort?.length ? verificationSort : DEFAULT_SORTED,
      columnFilters: verificationFilters,
      columnVisibility: verificationColumnVisibility,
      isLoading,
      isSaving: isPending
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        if ((e.target as HTMLElement).closest("button, a, input")) {
          return;
        }
        if (table.getState().editingRow) return;
        table.setEditingRow(row);
      }
    }),
    ...getMRTTopToolbarCustomActionsRenderer(),
    renderTopToolbarCustomActions: () => {
      return (
        <Group
          justify={toolbarComponents ? "space-between" : "flex-end"}
          ms="md"
          w="stretch"
        >
          {toolbarComponents}
          <Group>
            <IconButton
              disabled={
                isLoading ||
                !dataVerificationItems?.length ||
                dataVerificationItems.length === 0
              }
              icon={IconDownload}
              variant="filled"
              onClick={handleVerificationTableExport}
            >
              Exportieren
            </IconButton>
            <EneryRevenuesVerificationTableBatchActions
              batchIsDisabled={isPending || !hasTableSelection}
              handleChangeStatusBatchVerificationItems={
                handleChangeStatusBatchVerificationItems
              }
              handleDeleteBatchVerificationItems={
                handleDeleteBatchVerificationItems
              }
            />
          </Group>
        </Group>
      );
    },
    onSortingChange: (newSorting) => {
      const sortingValue =
        typeof newSorting === "function"
          ? newSorting(verificationSort)
          : newSorting;
      setVerificationSort(sortingValue);
    },
    onColumnFiltersChange: (newFilters) => {
      const filtersValue =
        typeof newFilters === "function"
          ? newFilters(verificationFilters)
          : newFilters;
      setVerificationFilters(filtersValue);
    },
    onColumnVisibilityChange: (newVisibility) => {
      const visibilityValue =
        typeof newVisibility === "function"
          ? newVisibility(verificationColumnVisibility)
          : newVisibility;
      setVerificationColumnVisibility(visibilityValue);
    },

    renderEmptyRowsFallback: () =>
      dataVerificationItems && dataVerificationItems.length > 0 ? (
        <NoDataMessage message="Keine Einträge gefunden." />
      ) : (
        <NoEntries
          customMessage={
            <>
              Um Daten in der <em>Prüfansicht</em> anzuzeigen, legen Sie bitte
              zuerst eine Prüfung in der Ansicht{" "}
              <em>&quot;Aktuelle Erlöse&quot;</em> an.
              <br />
              Kehren Sie dazu zurück und interagieren Sie mit den zu prüfenden
              Feldern. Anschließend werden die Informationen hier in der{" "}
              <em>Prüfansicht</em> sichtbar.
            </>
          }
          title="Prüfen Sie jetzt los!"
        />
      ),
    renderRowActions: ({ row, table }) => (
      <Group gap={0}>
        <EditButton
          label="Prüfung bearbeiten"
          onClick={() => {
            table.setEditingRow(row);
          }}
        />
        <HistoryButton onClick={() => setHistoryItem(row.original)} />
        <DeleteButton
          label="Prüfung löschen"
          onClick={() => {
            setVerificationItemsToDelete([row.original]);
          }}
        />
      </Group>
    ),
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveVerificationItem
  } satisfies Partial<MRT_TableOptions<EnergyRevenuesCellCheckStateResponse>>;

  const table = useMantineReactTable<EnergyRevenuesCellCheckStateResponse>({
    ...tableOptions,
    columns,
    data: dataVerificationItems || emptyArray
  });

  const hasTableSelection = table.getSelectedRowModel().flatRows.length > 0;

  return (
    <>
      <MantineReactTable table={table} />
      <VerificationHistoryModal
        verificationItemId={historyItem?.id}
        onClose={() => setHistoryItem(undefined)}
      />
      <DeleteConfirmationModal
        headerName={getVerificationLabel(verificationItemsToDelete?.length)}
        items={verificationItemsToDelete?.map(
          (item) =>
            `${item.period_start}: ${REVENUE_TABLE_HEADER_TEXTS[item.column]}`
        )}
        name={`${getVerificationLabel(verificationItemsToDelete?.length, true)}`}
        opened={verificationItemsToDelete !== undefined}
        toggleModal={() => setVerificationItemsToDelete(undefined)}
        onConfirm={handleDeleteVerificationItems}
      />
    </>
  );
}

export {
  EnergyRevenuesVerificationTable,
  EnergyRevenuesVerificationTableProps
};
